<template>
  <div>
    <select-field
        ref="AdminSummary"
        :value=display
        :required=required
        :edit-field-info=editFieldInfo
        :items=items
        :disabled=readOnly
        :can-edit=true
        :select-text=selectText
        :hide-label='hideLabel'
        :data-table='dataTable'
        :show-missing=showMissing
        :not-ordered=notOrdered
        @updated=setUpdated
        @on-edit=onEdit
        @error=setError>
    </select-field>

    <section-container
        v-if="showSection"
        :dialog-open="showSection"
        :extended="true"
        :fullscreen="section.fullscreen"
        :section="section"
        @close-dialog="closeDialog"
        @updated="setSectionUpdated">
    </section-container>
  </div>
</template>

<script lang="ts">
import Section from '@/framework/base/classes/Section';
import {FieldInfo, EditFieldType, FieldStatus} from '@/framework/base/components/FieldInfo'
import SummaryBase from '@/framework/base/components/SummaryBase';
import SelectField from '@/framework/base/components/SelectField.vue'
import Item from '@/framework/base/classes/Item';
import Admin, {AdminConfig, AdminObj, AdminObjActions} from '@/generated/classes/Admin';
import SectionContainer from '@/framework/base/components/SectionContainer.vue';
import SectionAdmin from './SectionAdmin.vue';
import ListData, {ListFilter} from '@/generated/classes/ListData';
import AdminAttrib from '@/generated/classes/AdminAttrib';
import AdminInfo from '@/generated/classes/AdminInfo';
import {ObjTypes} from '@/generated/classes/ObjTypes';
import {defineComponent} from "vue";

export default defineComponent({
  components: {
    SelectField,
    SectionContainer,
  },
  extends: SummaryBase,
  props: {
    helper: {
      type: Boolean
    },
    tableName: {
      type: String,
      required: true
    },
    tableClass: {
      type: String,
      required: false
    },
    objName: {
      type: String,
      required: true
    },
    publiContext: {
      type: String,
      required: true
    }
  },
  data() {
    const selectedItem: unknown = null;

    return {
      newContext: true,
      reset: true,
      listUpdated: false,
      listData: new ListData(),
      selectedItem,
      showSection: false
    };
  },
  computed: {
    editFieldInfo(): FieldInfo {
      const type = EditFieldType.SUMMARY;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
    },
    adminConfig(): AdminConfig {
      return this.listData.adminConfig;
    },
    adminAttribs(): AdminAttrib[] {
      return this.adminConfig.fields;
    },
    listItems(): ListData {
      if (this.reset != this.toggleReset) {
        this.loadItems();
      }
      return this.listData;
    },
    items(): Item[] {
      const items: Item[] = [];
      items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouveau"));
      this.listItems.reccords.forEach((item: any) => {
        items.push(new Item(item.id, this.getDisplay(item)));
      })
      return items;
    },
    display(): string {
      const value = this.value;
      let item = this.items.find(a => a.key == value)
      this.changeContext();
      if (this.items.length == 2 && this.newContext && this.required) {
        this.setNewContext(false);
        item = this.items.find(a => a.key == this.items[1].key);
        if (item && item.key != this.initValue) {
          this.itemChanged(item.key);
          this.setStatusUpdated(true);
          if (this.initValue != 0 || this.items.length != 2) {
            this.statusUpdated(this.status);
          }
        }
      }
      if (item) {
        if (this.resetToggle()) {
          this.setStatusUpdated(true);
          this.statusUpdated(this.status);
        }
        const obj = this.listItems.reccords.find((res: any) => item ? res.id == item.key : false);
        if (obj) {
          this.setSelectedItem(obj);
        }
      }
      const res = item && item.key != 0 ? item.title : '';
      if (this.cancelChanges && item) {
        this.itemChanged(item.key);
      }
      return res
    },
    section(): Section {
      const section = new Section();
      section.component = SectionAdmin;
      section.type = 'data';
      section.data = this.selectedItem;
      section.propType = 'config';
      section.propData = this.adminAttribs;
      section.adminInfo = this.adminInfo;
      return section;
    },
    adminInfo(): AdminInfo {
      const adminInfo = new AdminInfo();
      // @ts-expect-error : generated method
      adminInfo.objType = ObjTypes[this.tableClass];
      adminInfo.objName = this.objName;
      adminInfo.label = this.tableName;
      adminInfo.publiContext = this.publiContext;
      return adminInfo;
    }
  },
  methods: {
    setSelectedItem(obj: any) {
      this.selectedItem = obj;
    },
    setNewContext(value: boolean) {
      this.newContext = value;
    },
    setStatusUpdated(value: boolean) {
      this.status.updated = value;
    },
    loadItems(): Promise<boolean> {
      this.reset = this.toggleReset;
      const filter = new ListFilter();
      filter.tableName = this.tableName;
      return new Promise((resolve) => {
        Admin.loadTable(filter).then((res) => {
          this.listData = new ListData();
          this.listData = res;
          this.listUpdated = true;
          document.body.style.cursor = 'default';
          resolve(true);
        }).catch(() => {
          //Catch empty list
        });
      })
    },
    setSaveOrCancel() {
      this.cancelChanges = true;
    },
    getDisplay(item: any): string {
      return item.display;
    },
    setSectionUpdated(item: any) {
      const adminObj = new AdminObj();
      adminObj.table = this.tableName;
      adminObj.data = item;
      let res = this.items.find(a => a.key == item.id);
      if (res) {
        adminObj.action = AdminObjActions.UPDATE;
        Admin.updateTable(adminObj).then(() => {
          this.loadItems().then(() => {
            if (res) {
              this.setUpdated(res);
            }
          });
        });
      } else {
        adminObj.action = AdminObjActions.CREATE;
        Admin.updateTable(adminObj).then((obj: any) => {
          this.loadItems().then(() => {
            res = this.items.find(a => a.key == obj.id);
            if (res) {
              this.setUpdated(res);
            }
          });
        });
      }
    },
    setUpdated(item: Item): void {
      if (item.key == 0) {
        this.selectedItem = {};
        this.showSection = true;
      }
      this.itemChanged(item.key);
      this.status.updated = this.initValue != item.key;
      this.status.error = false;
      this.statusUpdated(this.status);
    },
    onEdit() {
      this.showSection = true;
    },
    closeDialog() {
      this.showSection = false;
    },
    itemChanged(id: number): void {
      this.$emit('update:value', id);
    },
    statusUpdated(status: FieldStatus): void {
      this.$emit('status', status);
    },
    missing(): boolean {
      // @ts-expect-error : generated method
      return this.$refs.AdminSummary.missing();
    },
    touch(): boolean {
      // @ts-expect-error : generated method
      return this.$refs.AdminSummary.touch();
    }
  }
})

</script>
